import queryString from 'query-string'
import { isEmpty, isNil, toNumber } from 'lodash'
import moment from 'moment'

import { toast } from '@/components/toasts/toast.helpers'
import {
  getFilterQueryString,
  getTagsWithCategories,
  explodeItemsByUser,
  getPercentNumerical,
  findPreviousDateRangeFromCurrentFilters,
  getFilterBodyString,
  getAgentOptions,
  aggregateByItemName,
  aggregateItemsByDate,
  aggregateItemsByUser,
  deduplicateItems,
  explodeItemsByOrganization,
  getMultipleOrgsSelected,
} from '@/utils/helpers'
import { fetchingAPI, apiService } from '@/api'
import { customStaticRanges } from '@/components/helpers'
import { ANALYTICS_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { fetchSavedFilters } from '@/reducers/savedFilters/savedFilters.actions'
import { fetchVoipCampaignsAPI } from '@/reducers/callSearch/callSearch.actions'
import { findNodeByValue } from '@/components/filters/filter.helpers'
import { ANALYTICS_SECTIONS } from '@/views/Analytics/analytics.constants'

import {
  setInitialLoadComplete,
  setLoading,
  setFilter,
  setCallsViewFilters,
  setDateRangeFilter,
  setData,
  clearReportData,
  clearFilters,
  clearCallsViewFilters,
  clearFiltersMinusOrg,
} from './analytics.redux'

const { CHECKLIST, DYNAMIC_PROMPT, NOTIFICATIONS, POSTCALL, USAGE, CSV } = ANALYTICS_SECTIONS

async function applyOverviewPageFilters(
  dispatch,
  filterString,
  filterBodyString,
  periodFilterString,
  multipleOrgsSelected
) {
  dispatch(clearReportData())
  dispatch(setLoading({ overviewPage: true }))

  try {
    const [callStatsResponse, totalUserCountResponse] = await Promise.all([
      fetchingAPI(`${apiService.reporting}/api/calls/stats?${filterString}`, 'GET', dispatch),
      fetchingAPI(`${apiService.reporting}/api/users/total_count?${filterString}`, 'GET', dispatch),
    ])

    const winPercent = getPercentNumerical(
      callStatsResponse.win_count,
      callStatsResponse.calls_count
    )

    dispatch(
      setData({
        callsCount: callStatsResponse.calls_count,
        winCount: callStatsResponse.win_count,
        averageHandleTime: callStatsResponse.average_handle_time,
        activeUserCount: callStatsResponse.active_user_count,
        totalUserCount: totalUserCountResponse,
        winPercent,
      })
    )

    if (multipleOrgsSelected) {
      const [checklistUsageByOrgResponse, deckItemsByOrgResponse, checklistUsageResponse] =
        await Promise.all([
          fetchingAPI(
            `${apiService.reporting}/api/checklist/usage_by_organization?${filterString}`,
            'GET',
            dispatch
          ),
          fetchingAPI(
            `${apiService.reporting}/api/dynamic_prompt/items_by_organization`,
            'POST',
            dispatch,
            filterBodyString
          ),
          fetchingAPI(
            `${apiService.reporting}/api/checklist/usage?${filterString}`,
            'GET',
            dispatch
          ),
        ])

      const explodedDeckOrgData = explodeItemsByOrganization(
        deckItemsByOrgResponse.items_by_organization,
        deckItemsByOrgResponse.organizations
      )

      const deckItems = deckItemsByOrgResponse.items_by_organization.map((entry) => entry.item)

      dispatch(
        setData({
          checklistUsage: checklistUsageResponse,
          topDeckItems: { by_count: [], by_percent: [] },
          checklistUsageByOrganization: checklistUsageByOrgResponse,
          deckItemsByOrganization: explodedDeckOrgData,
          deckItems,
        })
      )
    } else {
      const [checklistUsageResponse, topNotificationsAndDecksResponse] = await Promise.all([
        fetchingAPI(`${apiService.reporting}/api/checklist/usage?${filterString}`, 'GET', dispatch),
        fetchingAPI(
          `${apiService.reporting}/api/playbooks/top_notifications_and_decks?${filterString}`,
          'GET',
          dispatch
        ),
      ])

      dispatch(
        setData({
          checklistUsage: checklistUsageResponse,
          topDeckItems: topNotificationsAndDecksResponse.top_deck_items,
          checklistUsageByOrganization: [],
          deckItemsByOrganization: [],
        })
      )
    }
  } catch (err) {
    toast.error('Failed to apply filters', { cause: err })
  } finally {
    dispatch(setLoading({ overviewPage: false }))
  }

  dispatch(setLoading({ overviewPageComparisons: true }))

  try {
    const [
      previousCallStatsResponse,
      previousTotalUserCountResponse,
      previousChecklistUsageResponse,
    ] = await Promise.all([
      fetchingAPI(`${apiService.reporting}/api/calls/stats?${periodFilterString}`, 'GET', dispatch),
      fetchingAPI(
        `${apiService.reporting}/api/users/total_count?${periodFilterString}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.reporting}/api/checklist/usage?${periodFilterString}`,
        'GET',
        dispatch
      ),
    ])

    // Win Percent
    const previousWinPercent = getPercentNumerical(
      previousCallStatsResponse.win_count,
      previousCallStatsResponse.calls_count
    )

    dispatch(
      setData({
        previousCallsCount: previousCallStatsResponse.calls_count,
        previousWinCount: previousCallStatsResponse.win_count,
        previousAverageHandleTime: previousCallStatsResponse.average_handle_time,
        previousActiveUserCount: previousCallStatsResponse.active_user_count,
        previousTotalUserCount: previousTotalUserCountResponse,
        previousChecklistUsage: previousChecklistUsageResponse,
        previousWinPercent,
      })
    )
  } catch (err) {
    console.error(err)
  } finally {
    dispatch(setLoading({ overviewPageComparisons: false }))
  }
}

async function applyChecklistPageFilters(
  dispatch,
  filterString,
  own_organization_id,
  multipleOrgsSelected
) {
  dispatch(clearReportData())
  dispatch(setLoading({ checklistPage: true }))

  try {
    if (multipleOrgsSelected) {
      const [usageByOrgResponse, checklistItemsByOrgResponse] = await Promise.all([
        fetchingAPI(
          `${apiService.reporting}/api/checklist/usage_by_organization?${filterString}`,
          'GET',
          dispatch
        ),
        fetchingAPI(
          `${apiService.reporting}/api/checklist/items_by_organization?${filterString}`,
          'GET',
          dispatch
        ),
      ])

      const explodedOrgData = explodeItemsByOrganization(
        checklistItemsByOrgResponse.items_by_organization,
        checklistItemsByOrgResponse.organizations
      )

      const checklistItems = checklistItemsByOrgResponse.items_by_organization.map(
        (entry, index) => ({
          item: entry.item,
          win: 'false',
          order: index + 1,
        })
      )

      dispatch(
        setData({
          checklistUsageByOrganization: usageByOrgResponse,
          checklistItemsByOrganization: explodedOrgData,
          checklistItems,
        })
      )
    } else {
      dispatch(setData({ checklistUsageByOrganization: [] }))

      const [
        checklistItemsResponse,
        checklistItemsByDateResponse,
        checklistItemsByUserResponse,
        checklistItemsByCallResponse,
      ] = await Promise.all([
        fetchingAPI(`${apiService.reporting}/api/checklist/items?${filterString}`, 'GET', dispatch),
        fetchingAPI(
          `${apiService.reporting}/api/checklist/items_by_date?${filterString}`,
          'GET',
          dispatch
        ),
        fetchingAPI(
          `${apiService.reporting}/api/checklist/items_by_user?${filterString}`,
          'GET',
          dispatch
        ),
        fetchingAPI(
          `${apiService.reporting}/api/checklist/items_by_call?${filterString}`,
          'GET',
          dispatch
        ).catch((err) => {
          if (own_organization_id === 1) {
            // only catch error for org 1
            // items_by_call api can return a transcript and fails if org 1 queries this page and the org being queried is unredacted
            return { calls_data: [], older_cursor: null, newer_cursor: null }
          }
          throw err
        }),
      ])

      const explodedData = explodeItemsByUser(
        checklistItemsByUserResponse.items_by_user,
        checklistItemsByUserResponse.users,
        CHECKLIST
      )

      dispatch(
        setData({
          checklistItems: checklistItemsResponse,
          checklistUsage: checklistItemsByDateResponse.usage_by_date,
          checklistUsageByItem: checklistItemsByDateResponse.usage_by_item,
          checklistItemsByUser: explodedData,
          checklistItemsByDate: checklistItemsByDateResponse.items_by_date,
          checklistItemsByCall: checklistItemsByCallResponse.calls_data,
          checklistItemsByCallCursors: {
            olderCursor: checklistItemsByCallResponse.older_cursor,
            newerCursor: checklistItemsByCallResponse.newer_cursor,
          },
        })
      )
    }
  } catch (err) {
    toast.error('Failed to apply filters', { cause: err })
  } finally {
    dispatch(setLoading({ checklistPage: false }))
  }
}

async function applyDeckPageFilters(
  dispatch,
  filterQueryString,
  filterBodyString,
  own_organization_id,
  multipleOrgsSelected
) {
  dispatch(clearReportData())
  dispatch(
    setLoading({
      deckPage: true,
      deckPageWins: true,
    })
  )

  try {
    await Promise.all([
      fetchingAPI(
        `${apiService.reporting}/api/dynamic_prompt/items`,
        'POST',
        dispatch,
        filterBodyString
      ),
      fetchingAPI(
        `${apiService.reporting}/api/dynamic_prompt/items_by_date`,
        'POST',
        dispatch,
        filterBodyString
      ),
      multipleOrgsSelected
        ? fetchingAPI(
            `${apiService.reporting}/api/dynamic_prompt/items_by_organization`,
            'POST',
            dispatch,
            filterBodyString
          )
        : fetchingAPI(
            `${apiService.reporting}/api/dynamic_prompt/items_by_user`,
            'POST',
            dispatch,
            filterBodyString
          ),
      multipleOrgsSelected
        ? Promise.resolve({ calls_data: [], older_cursor: null, newer_cursor: null })
        : fetchingAPI(
            `${apiService.reporting}/api/dynamic_prompt/items_by_call`,
            'POST',
            dispatch,
            filterBodyString
          ).catch((err) => {
            if (own_organization_id === 1) {
              // only catch error for org 1
              // items_by_call api can return a transcript and fails if org 1 queries this page and the org being queried is unredacted
              return { calls_data: [], older_cursor: null, newer_cursor: null }
            }
            throw err
          }),
      fetchingAPI(
        `${apiService.reporting}/api/usage/decklist_response_count_by_date`,
        'POST',
        dispatch,
        filterBodyString
      ),
      multipleOrgsSelected
        ? fetchingAPI(
            `${apiService.reporting}/api/usage/decklist_response_count_by_date_and_org`,
            'POST',
            dispatch,
            filterBodyString
          )
        : fetchingAPI(
            `${apiService.reporting}/api/usage/decklist_response_count_by_date_and_user?${filterQueryString}`,
            'GET',
            dispatch
          ),
      multipleOrgsSelected
        ? Promise.resolve(null)
        : fetchingAPI(
            `${apiService.reporting}/api/dynamic_prompt/win_rate`,
            'POST',
            dispatch,
            filterBodyString
          ),
    ]).then(
      ([
        deckItemsResponse,
        deckItemsByDateResponse,
        deckItemsByUserOrOrgResponse,
        deckItemsByCallResponse,
        decklistResponseCountByDateResponse,
        decklistResponseCountByDateAndUserResponse,
        deckWinRateResponse,
      ]) => {
        const sharedData = {
          deckItems: deduplicateItems(deckItemsResponse),
          deckUsage: deckItemsByDateResponse.usage_by_date,
          deckUsageByItem: aggregateByItemName(deckItemsByDateResponse.usage_by_item),
          deckItemsByDate: aggregateItemsByDate(deckItemsByDateResponse.items_by_date),
          deckItemsByCall: deckItemsByCallResponse.calls_data,
          responseCountsByDate: decklistResponseCountByDateResponse,
          deckItemsByCallCursors: {
            olderCursor: deckItemsByCallResponse.older_cursor,
            newerCursor: deckItemsByCallResponse.newer_cursor,
          },
        }

        if (multipleOrgsSelected) {
          const explodedOrgData = explodeItemsByOrganization(
            deckItemsByUserOrOrgResponse.items_by_organization,
            deckItemsByUserOrOrgResponse.organizations
          )

          dispatch(
            setData({
              ...sharedData,
              deckItemsByUser: [],
              deckItemsByOrganization: explodedOrgData,
              responseCountsByDateAndUser: [],
              responseCountsByDateAndOrg: decklistResponseCountByDateAndUserResponse,
            })
          )
        } else {
          const explodedData = explodeItemsByUser(
            deckItemsByUserOrOrgResponse.items_by_user,
            deckItemsByUserOrOrgResponse.users,
            DYNAMIC_PROMPT
          )

          dispatch(
            setData({
              ...sharedData,
              deckItemsByUser: aggregateItemsByUser(explodedData),
              deckItemsByOrganization: [],
              responseCountsByDateAndUser: decklistResponseCountByDateAndUserResponse,
              responseCountsByDateAndOrg: [],
            })
          )
        }

        if (deckWinRateResponse) {
          dispatch(setData({ deckWinRate: deckWinRateResponse }))
        }

        dispatch(setLoading({ deckPage: false, deckPageWins: false }))
      }
    )
  } catch (err) {
    toast.error(err?.message || 'Failed to apply filters')
    dispatch(
      setLoading({
        deckPage: false,
        deckPageWins: false,
      })
    )
  }
}

async function applyNotificationsPageFilters(dispatch, filterString, own_organization_id) {
  dispatch(setLoading({ notificationsPage: true }))

  try {
    const [
      notificationsResponse,
      notificationsByDateResponse,
      notificationsByUserResponse,
      notificationsItemsByCallResponse,
    ] = await Promise.all([
      fetchingAPI(
        `${apiService.reporting}/api/notifications/items?${filterString}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.reporting}/api/notifications/items_by_date?${filterString}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.reporting}/api/notifications/items_by_user?${filterString}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.reporting}/api/notifications/items_by_call?${filterString}`,
        'GET',
        dispatch
      ).catch((err) => {
        if (own_organization_id === 1) {
          // only catch error for org 1
          // items_by_call api can return a transcript and fails if org 1 queries this page and the org being queried is unredacted
          return { calls_data: [], older_cursor: null, newer_cursor: null }
        }
        throw err
      }),
    ])
    const explodedData = explodeItemsByUser(
      notificationsByUserResponse.items_by_user,
      notificationsByUserResponse.users,
      NOTIFICATIONS
    )
    dispatch(
      // easier to just keep "Items" even if it doesn't make sense here
      setData({
        notificationsItems: notificationsResponse,
        notificationsUsage: notificationsByDateResponse.usage_by_date,
        notificationsUsageByItem: notificationsByDateResponse.usage_by_item,
        notificationsItemsByDate: notificationsByDateResponse.items_by_date,
        notificationsItemsByUser: explodedData,
        notificationsItemsByCall: notificationsItemsByCallResponse.calls_data,
        notificationsItemsByCallCursors: {
          olderCursor: notificationsItemsByCallResponse.older_cursor,
          newerCursor: notificationsItemsByCallResponse.newer_cursor,
        },
      })
    )
  } catch (err) {
    toast.error('Failed to apply filters', { cause: err })
  } finally {
    dispatch(setLoading({ notificationsPage: false }))
  }
}

async function applyPostCallPageFilters(
  dispatch,
  filterString,
  own_organization_id,
  multipleOrgsSelected
) {
  dispatch(clearReportData())
  dispatch(setLoading({ postcallPage: true }))

  try {
    const [
      postcallResponse,
      postcallByDateResponse,
      postcallItemsByUserOrOrgResponse,
      postcallItemsByCallResponse,
    ] = await Promise.all([
      fetchingAPI(`${apiService.reporting}/api/postcall/items?${filterString}`, 'GET', dispatch),
      fetchingAPI(
        `${apiService.reporting}/api/postcall/items_by_date?${filterString}`,
        'GET',
        dispatch
      ),
      multipleOrgsSelected
        ? fetchingAPI(
            `${apiService.reporting}/api/postcall/items_by_organization?${filterString}`,
            'GET',
            dispatch
          )
        : fetchingAPI(
            `${apiService.reporting}/api/postcall/items_by_user?${filterString}`,
            'GET',
            dispatch
          ),
      multipleOrgsSelected
        ? Promise.resolve({ calls_data: [], older_cursor: null, newer_cursor: null })
        : fetchingAPI(
            `${apiService.reporting}/api/postcall/items_by_call?${filterString}`,
            'GET',
            dispatch
          ).catch((err) => {
            if (own_organization_id === 1) {
              // items_by_call api can return a transcript and fails if org 1 queries this page and the org being queried is unredacted
              return { calls_data: [], older_cursor: null, newer_cursor: null }
            }
            throw err
          }),
    ])

    const sharedData = {
      postcallItems: deduplicateItems(postcallResponse),
      postcallUsage: postcallByDateResponse.usage_by_date,
      postcallUsageByItem: aggregateByItemName(postcallByDateResponse.usage_by_item),
      postcallItemsByDate: aggregateItemsByDate(postcallByDateResponse.items_by_date),
      postcallItemsByCall: postcallItemsByCallResponse.calls_data,
      postcallItemsByCallCursors: {
        olderCursor: postcallItemsByCallResponse.older_cursor,
        newerCursor: postcallItemsByCallResponse.newer_cursor,
      },
    }

    if (multipleOrgsSelected) {
      const explodedOrgData = explodeItemsByOrganization(
        postcallItemsByUserOrOrgResponse.items_by_organization,
        postcallItemsByUserOrOrgResponse.organizations
      )

      dispatch(
        setData({
          ...sharedData,
          postcallItemsByUser: [],
          postcallItemsByOrganization: explodedOrgData,
        })
      )
    } else {
      const explodedData = explodeItemsByUser(
        postcallItemsByUserOrOrgResponse.items_by_user,
        postcallItemsByUserOrOrgResponse.users,
        POSTCALL
      )

      dispatch(
        setData({
          ...sharedData,
          postcallItemsByUser: aggregateItemsByUser(explodedData),
          postcallItemsByOrganization: [],
        })
      )
    }
  } catch (err) {
    toast.error('Failed to apply filters', { cause: err })
  } finally {
    dispatch(setLoading({ postcallPage: false }))
  }
}

async function fetchContractedSeats(dispatch, filterString) {
  try {
    const contractedSeatsResponse = await fetchingAPI(
      `${apiService.reporting}/api/usage/contracted_seats?${filterString}`,
      'GET',
      dispatch
    )

    dispatch(setData({ contractedSeats: contractedSeatsResponse || [] }))
  } catch (err) {
    dispatch(setData({ contractedSeats: [] }))
    toast.error('Failed to load contracted seats', { cause: err })
  } finally {
    dispatch(setLoading({ contractedSeats: false }))
  }
}

async function applyUsagePageFilters(dispatch, filterString, previousPeriodFilters) {
  dispatch(setLoading({ usagePage: true, contractedSeats: true }))

  fetchContractedSeats(dispatch, filterString)

  try {
    const [
      userCountsByDateResponse,
      callCountsByDateResponse,
      callCountsByUserResponse,
      callCountsByUserPreviousPeriodResponse,
    ] = await Promise.all([
      fetchingAPI(
        `${apiService.reporting}/api/usage/user_counts_by_date?${filterString}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.reporting}/api/usage/call_counts_by_date?${filterString}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.reporting}/api/usage/users_activity?${filterString}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.reporting}/api/usage/users_activity?${previousPeriodFilters}`,
        'GET',
        dispatch
      ),
    ])

    dispatch(
      setData({
        userCountsByDate: userCountsByDateResponse,
        callCountsByDate: callCountsByDateResponse,
        callCountsByUser: callCountsByUserResponse.all_users || [],
        usersWithoutCalls: callCountsByUserResponse.users_without_calls || [],
        callCountsByUserPreviousPeriod: callCountsByUserPreviousPeriodResponse.all_users || [],
      })
    )
  } catch (err) {
    toast.error('Failed to apply filters', { cause: err })
  } finally {
    dispatch(setLoading({ usagePage: false }))
  }
}

export const fetchTagsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ tags: true }))

  try {
    const [tags, tagCategories] = await Promise.all([
      fetchingAPI(`${apiService.core}/api/organizations/${organizationId}/tags`, 'GET', dispatch),
      fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags/categories`,
        'GET',
        dispatch
      ),
    ])

    const tagsOptionsWithCategories = getTagsWithCategories(tags, tagCategories)

    dispatch(setData({ tags: tagsOptionsWithCategories, fetchedTagsOrg: organizationId }))
  } catch (err) {
    toast.error('Failed to fetch tags', { cause: err })
  } finally {
    dispatch(setLoading({ tags: false }))
  }
}

export const fetchAgentsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ agents: true }))

  try {
    const { users } = await fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}/users`,
      'GET',
      dispatch
    )

    dispatch(setData({ agents: getAgentOptions(users), fetchedAgentsOrg: organizationId }))
  } catch (err) {
    toast.error('Failed to fetch agents', { cause: err })
  } finally {
    dispatch(setLoading({ agents: false }))
  }
}

export const fetchPlaybooksByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ playbooks: true }))

  try {
    const configProperties = queryString.stringify({ requested_properties: 'id,name,cid' })
    const playbooks = await fetchingAPI(
      `${apiService.core}/api/${organizationId}/configs?${configProperties}`,
      'GET',
      dispatch
    )
    const playbookOptions = playbooks
      .map((playbook) => ({
        value: playbook.cid,
        label: playbook.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    dispatch(setData({ playbooks: playbookOptions, fetchedPlaybooksOrg: organizationId }))
    return playbookOptions
  } catch (err) {
    toast.error('Failed to fetch playbooks', { cause: err })
    // Reports the failure to callers that need to tell "still loading" apart
    // from "will never arrive". A toast alone cannot: it is gone by the time
    // anyone opens a panel, and the slice is left holding whatever the last
    // successful fetch put there. Existing callers ignore the return value.
    return null
  } finally {
    dispatch(setLoading({ playbooks: false }))
  }
}

export const fetchPlaybooksByOrgs = (organizationIds, orgHierarchy) => async (dispatch) => {
  dispatch(setLoading({ playbooks: true }))

  try {
    const configProperties = queryString.stringify({ requested_properties: 'id,name,cid' })
    const results = await Promise.all(
      organizationIds.map(async (orgId) => {
        const playbooks = await fetchingAPI(
          `${apiService.core}/api/${orgId}/configs?${configProperties}`,
          'GET',
          dispatch
        )
        return { orgId, playbooks }
      })
    )

    const groupedPlaybooks = results.map(({ orgId, playbooks }) => {
      const orgNode = findNodeByValue(orgHierarchy, orgId)

      return {
        label: orgNode?.label || `Organization ${orgId}`,
        options: playbooks
          .map((playbook) => ({ value: playbook.cid, label: playbook.name }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      }
    })

    dispatch(setData({ playbooks: groupedPlaybooks, fetchedPlaybooksOrg: 'multiple' }))
  } catch (err) {
    toast.error('Failed to fetch playbooks', { cause: err })
  } finally {
    dispatch(setLoading({ playbooks: false }))
  }
}

export const fetchCategoriesByOrgSectionAndPlaybooks =
  (organizationId, section, playbooks = []) =>
  async (dispatch) => {
    dispatch(setLoading({ [`${section}Categories`]: true }))

    try {
      const filters = queryString.stringify({
        organization_id: organizationId,
        playbooks: playbooks.map((x) => x.value),
      })

      const categories = await fetchingAPI(
        `${apiService.core}/api/configs/${section}_categories?${filters}`,
        'GET',
        dispatch
      )
      const categoryOptions = categories[`${section}_categories`]
        .sort((a, b) => a.localeCompare(b))
        .map((cat) => ({ value: cat, label: cat }))

      dispatch(setData({ [`${section}Categories`]: categoryOptions }))
    } catch (err) {
      toast.error('Failed to fetch categories', { cause: err })
    } finally {
      dispatch(setLoading({ [`${section}Categories`]: false }))
    }
  }

export const fetchPlaybookData = (filters) => async (dispatch, getState) => {
  dispatch(setLoading({ playbookData: true }))
  const { organization: organization_id, startDate: start_date, endDate: end_date } = filters
  const { organizationid: currentUserOrgId } = getState().currentUser

  const orgIdToUse = organization_id || currentUserOrgId

  try {
    const playbookRes = await Promise.all(
      filters.playbooks.map(async (playbook) => {
        try {
          const body = {
            start_date,
            end_date,
            organization_id: orgIdToUse,
            config_cid: playbook.value,
          }
          return await fetchingAPI(
            `${apiService.reporting}/api/playbook_event_filters?config_cid=${playbook.value}`,
            'POST',
            dispatch,
            JSON.stringify(body)
          )
        } catch {
          return { config_cid: playbook.value, error: true }
        }
      })
    )
    const playbookResUpdated = playbookRes.filter((playbookRes) => {
      return isNil(playbookRes.error)
    })
    const updatedPlaybookData = []

    playbookResUpdated.forEach((playbook) => {
      playbook.deck = playbook?.deck?.map((category) => {
        const updatedSubcategories = category.items.map((item) => {
          if (!item.has_sub_items) {
            return item
          }
          const updatedItems = [...item.items, 'No Response Used']

          return { ...item, items: updatedItems }
        })

        return {
          ...category,
          items: updatedSubcategories,
          playbookName: playbook.playbook_name,
          cid: playbook.cid,
        }
      })
      updatedPlaybookData.push(playbook)
    })
    dispatch(
      setData({
        playbookData: updatedPlaybookData,
      })
    )
  } catch {
    // Do nothing!
  } finally {
    dispatch(setLoading({ playbookData: false }))
  }
}

export const fetchDispositionsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ dispositions: true }))

  try {
    const dispositions = await fetchingAPI(
      `${apiService.reporting}/api/dispositions?organization_id=${organizationId}`,
      'GET',
      dispatch
    )
    const dispoOptions = dispositions
      .map(({ name }) => ({ value: name, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))
    dispatch(setData({ dispositions: dispoOptions }))
  } catch (err) {
    toast.error('Failed to fetch dispositions', { cause: err })
  } finally {
    dispatch(setLoading({ dispositions: false }))
  }
}
export const fetchVoipCampaignsByOrg = (organizationId) => async (dispatch, getState) => {
  if (!organizationId) {
    return
  }
  dispatch(setLoading({ voipCampaigns: true }))

  try {
    const { startDate, endDate, voipCampaigns: currentFilters } = getState().analytics.filters

    const voipCampaignOptions = await fetchVoipCampaignsAPI(
      organizationId,
      startDate,
      endDate,
      dispatch
    )
    const newVoipCampaignFilters = currentFilters.filter((f) => {
      return voipCampaignOptions.some((option) => option.value === f.value)
    })
    dispatch(setFilter({ voipCampaigns: newVoipCampaignFilters }))
    dispatch(setData({ voipCampaigns: voipCampaignOptions }))
  } catch (err) {
    toast.error('Failed to fetch VOIP campaigns', { cause: err })
  } finally {
    dispatch(setLoading({ voipCampaigns: false }))
  }
}

export const fetchAllData = (organizationId) => async (dispatch, getState) => {
  const {
    currentUser,
    analytics: { data },
  } = getState()

  // Load default data for Balto Admin
  if (currentUser.organizationid === 1 && organizationId === 1) {
    dispatch(setFilter({ organization: organizationId }))
  }
  if (data.fetchedTagsOrg !== organizationId) {
    dispatch(fetchTagsByOrg(organizationId))
  }
  // Hierarchy managers get their playbooks from AnalyticsFilters instead, which knows whether the
  // org selector is driving the fetch or the org has no hierarchy to select from. Balto admins are
  // excluded on own_organization_id, not organizationid, because the latter follows whichever org
  // the user switched to - an admin viewing another org would otherwise be skipped by both paths
  if (
    data.fetchedPlaybooksOrg !== organizationId &&
    !(currentUser.hierarchy_manager && currentUser.own_organization_id !== 1)
  ) {
    dispatch(fetchPlaybooksByOrg(organizationId))
  }
  if (data.fetchedAgentsOrg !== organizationId) {
    dispatch(fetchAgentsByOrg(organizationId))
  }
  if (isEmpty(data.deckCategories)) {
    dispatch(fetchCategoriesByOrgSectionAndPlaybooks(organizationId, DYNAMIC_PROMPT))
  }
  if (isEmpty(data.postcallCategories)) {
    dispatch(fetchCategoriesByOrgSectionAndPlaybooks(organizationId, POSTCALL))
  }
  if (isEmpty(data.dispositions)) {
    dispatch(fetchDispositionsByOrg(organizationId))
  }
  if (isEmpty(data.voipCampaigns)) {
    dispatch(fetchVoipCampaignsByOrg(organizationId))
  }
}

/**
 * Pagination of calls
 *
 * In lieu of using the traditional page/total/offset system of pagination, this expensive operation
 * is using a more performant database cursor, which allows us to pass a pointer to an ordered set
 * and get the next set of results, including whether or not we can continue forward or backward.
 *
 * Passing no cursor = newest results
 * Passing the newer_cursor = newer set of results
 * Passing the older_cursor = older set of results
 * Passing no cursor and oldest=1 = oldest results
 *
 * @param {string} cursor Unique string pointing to current position in the set of results
 * @param {number} oldest whether or not to show the least recent results
 * @param {string} section string indicating which section of analytics data to apply changes to
 */
export const paginateItemsByCall =
  (cursor = '', oldest = '', section) =>
  async (dispatch, getState) => {
    const { analytics } = getState()
    const filterString = getFilterQueryString(analytics.filters, section)
    let callsViewFilterString = ''

    dispatch(setLoading({ [`${section}ItemsByCall`]: true }))

    if (section === DYNAMIC_PROMPT) {
      callsViewFilterString = `&${queryString.stringify(analytics.callsViewFilters)}`
    }

    try {
      const url_section = section === DYNAMIC_PROMPT ? 'dynamic_prompt' : section
      const response = await fetchingAPI(
        `${apiService.reporting}/api/${url_section}/items_by_call?${filterString}${callsViewFilterString}&pagination_cursor=${cursor}&pagination_oldest=${oldest}`,
        'GET',
        dispatch
      )

      dispatch(
        setData({
          [`${section}ItemsByCall`]: response.calls_data,
          [`${section}ItemsByCallCursors`]: {
            olderCursor: response.older_cursor,
            newerCursor: response.newer_cursor,
          },
        })
      )
    } catch (err) {
      toast.error('Failed to fetch call data', { cause: err })
    } finally {
      dispatch(setLoading({ [`${section}ItemsByCall`]: false }))
    }
  }

export const applyFilters = (section) => async (dispatch, getState) => {
  // clear any sub-filters that may be on the page
  dispatch(clearCallsViewFilters())

  const { analytics } = getState()
  const { own_organization_id, hierarchy_manager: isHierarchyManager } = getState().currentUser
  const { flattenedUserOrgHierarchy } = getState().orgHierarchy
  const isBaltoAdmin = own_organization_id === 1
  const multipleOrgsSelected = getMultipleOrgsSelected({
    isBaltoAdmin,
    isHierarchyManager,
    flattenedUserOrgHierarchy,
    organizations: analytics.filters.organizations,
  })

  const filterQueryString = getFilterQueryString(analytics.filters, section)
  const filterBodyString = getFilterBodyString(analytics.filters, section)
  const [newStartDate, newEndDate] = findPreviousDateRangeFromCurrentFilters(
    analytics.filters.startDate,
    analytics.filters.endDate
  )

  const periodFilterString = getFilterQueryString(
    { ...analytics.filters, startDate: newStartDate, endDate: newEndDate },
    section
  )

  switch (section) {
    case CHECKLIST:
      await applyChecklistPageFilters(
        dispatch,
        filterQueryString,
        own_organization_id,
        multipleOrgsSelected
      )
      break
    case DYNAMIC_PROMPT:
      await applyDeckPageFilters(
        dispatch,
        filterQueryString,
        filterBodyString,
        own_organization_id,
        multipleOrgsSelected
      )
      break
    case NOTIFICATIONS:
      await applyNotificationsPageFilters(dispatch, filterQueryString, own_organization_id)
      break
    case POSTCALL:
      await applyPostCallPageFilters(
        dispatch,
        filterQueryString,
        own_organization_id,
        multipleOrgsSelected
      )
      break
    case USAGE:
      await applyUsagePageFilters(dispatch, filterQueryString, periodFilterString)
      break
    case CSV:
      break
    default:
      await applyOverviewPageFilters(
        dispatch,
        filterQueryString,
        filterBodyString,
        periodFilterString,
        multipleOrgsSelected
      )
  }
}

export const clearSavedFilter = () => (dispatch, getState) => {
  const { currentUser } = getState()
  const isBaltoAdmin = currentUser.organizationid === 1

  dispatch(setData({ selectedSavedFilterId: null }))

  if (isBaltoAdmin) {
    dispatch(clearFiltersMinusOrg())
  } else {
    dispatch(clearFilters())
  }

  dispatch(clearReportData())
}

export const setSavedFilterById =
  (filterId, shouldApplyFilters, section) => (dispatch, getState) => {
    const {
      savedFilters: { savedFilterList },
    } = getState()
    const analyticsSavedFilters = savedFilterList[ANALYTICS_FILTER_TYPE]
    const filterToApply = analyticsSavedFilters.find((filter) => filter.uuid === filterId)

    if (filterToApply?.filters) {
      dispatch(setData({ selectedSavedFilterId: filterId }))
      dispatch(setFilter(filterToApply.filters))

      // Set actual start/end times based on static range
      const staticRange = customStaticRanges.find(
        (staticRange) => staticRange.label.toLowerCase() === filterToApply.filters.dateRange
      )

      if (staticRange) {
        dispatch(setDateRangeFilter(staticRange.range()))
      }
    }

    if (shouldApplyFilters) {
      dispatch(applyFilters(section))
    }
  }
export const setFilterOrganization = (organizationId) => (dispatch) => {
  dispatch(setFilter({ organization: organizationId }))
}
export const setDefaultSavedFilter = () => (dispatch, getState) => {
  const {
    savedFilters: { savedFilterList },
  } = getState()
  const analyticsSavedFilters = savedFilterList[ANALYTICS_FILTER_TYPE]

  // Find the default saved filter, if exists
  const defaultFilter = analyticsSavedFilters.find((filter) => filter.is_default)

  // Set the saved filter options if they exist
  if (defaultFilter) {
    dispatch(setSavedFilterById(defaultFilter.uuid))
  }
}

export const applyCallsViewFilters = (props) => async (dispatch, getState) => {
  const { deckItems, withEvents } = props

  dispatch(setLoading({ deckItemsByCall: true }))
  const {
    analytics: { filters },
  } = getState()
  const filterString = getFilterQueryString(filters, DYNAMIC_PROMPT)
  const callsViewFilterString = queryString.stringify({
    entries: deckItems,
    with_events: withEvents,
  })
  dispatch(setCallsViewFilters({ entries: deckItems, with_events: withEvents }))

  try {
    const deckItemsByCallResponse = await fetchingAPI(
      `${apiService.reporting}/api/dynamic_prompt/items_by_call?${filterString}&${callsViewFilterString}`,
      'GET',
      dispatch
    )

    dispatch(
      setData({
        deckItemsByCall: deckItemsByCallResponse.calls_data,
        deckItemsByCallCursors: {
          olderCursor: deckItemsByCallResponse.older_cursor,
          newerCursor: deckItemsByCallResponse.newer_cursor,
        },
      })
    )
  } catch (err) {
    toast.error('Failed to apply calls view filters', { cause: err })
  } finally {
    dispatch(setLoading({ deckItemsByCall: false }))
  }
}

export const loadInitialData = () => async (dispatch, getState) => {
  const { organizationid: currentUserOrgId, hierarchy_manager: isHierarchyManager } =
    getState().currentUser
  const { selectedHierarchyOrganization } = getState().analytics
  const selectedOrg = selectedHierarchyOrganization ?? currentUserOrgId
  await dispatch(setFilterOrganization(selectedOrg))

  if (!isHierarchyManager) {
    await dispatch(fetchSavedFilters(ANALYTICS_FILTER_TYPE))
    dispatch(setDefaultSavedFilter())
  }

  // not awaiting these because it can be in the process of loading while the report data is fetched
  dispatch(fetchAllData(selectedHierarchyOrganization ?? currentUserOrgId))

  dispatch(setInitialLoadComplete())
}

// This old reporting endpoint passes the playbook config along in the body instead of
// fetching it on the backend using the config cid so we have to do all these extra API calls.
export const preFetchCSVExport = () => async (dispatch, getState) => {
  const {
    analytics: { filters },
  } = getState()
  const playbookCid = filters.playbooks[0].value

  try {
    const versions = await fetchingAPI(
      `${apiService.core}/api/configs/${playbookCid}/versions`,
      'GET',
      dispatch
    )
    const latestVersionId = versions[0].id

    const playbook = await fetchingAPI(
      `${apiService.core}/api/configs/${latestVersionId}`,
      'GET',
      dispatch
    )

    return playbook
  } catch (err) {
    return toast.error('Failed to fetch data for CSV export', { cause: err })
  }
}

export const fetchCSVExport = (reportType, selectedSections, playbook) => (dispatch, getState) => {
  const {
    analytics: { filters },
  } = getState()

  dispatch(setLoading({ exportCSVPage: true }))

  const body = JSON.stringify({
    config: playbook.config,
    callDuration: toNumber(filters.callDuration),
    configCid: filters.playbooks[0].value,
    startDate: moment(filters.startDate).startOf('day').format(),
    endDate: moment(filters.endDate).endOf('day').format(),
    selectedTags: filters.tags.map((tag) => tag.value),
    includeManagers: filters.includeManagers,
    organizationId: filters.organization || null,
    selectedSections,
  })

  const filename = `Balto_${reportType}_${moment(filters.startDate).format('YYYY-MM-DD')}-${moment(
    filters.endDate
  ).format('YYYY-MM-DD')}.csv`

  return fetchingAPI(`${apiService.core}/api/reports/csv/${reportType}`, 'POST', dispatch, body)
    .then((csvStream) => {
      // get stream reader from fetch response body stream
      const responseReader = csvStream.getReader()
      const streamProgress = { calls: 0, data: '' }
      // this function reads stream, then if done == true we download and clean up.
      // if not then we read value, update accumulators, and recursively call
      const readStream = () =>
        responseReader.read().then(({ value, done }) => {
          if (done) {
            const encodedUri = URL.createObjectURL(
              new Blob([streamProgress.data], { type: 'text/csv' })
            )
            const link = document.createElement('a')
            link.setAttribute('href', encodedUri)
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            dispatch(setData({ callsDownloaded: 'Complete' }))
            dispatch(setLoading({ exportCSVPage: false }))

            return null
          }
          // stream comes in as uint8array
          const decodedCsvData = new TextDecoder('utf-8').decode(value)
          streamProgress.data += decodedCsvData
          streamProgress.calls += decodedCsvData.split('\n').filter((call) => call !== '').length

          dispatch(setData({ callsDownloaded: streamProgress.calls }))

          return readStream()
        })

      readStream()
    })
    .catch((err) => {
      if (err?.status === 422) {
        toast.error(err?.message)
      }

      dispatch(setLoading({ exportCSVPage: false }))
    })
}

export const fetchPromptsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ prompts: true }))

  try {
    const url = `${apiService.reporting}/api/lightning_bolts/organization/${organizationId}`
    const rawPrompts = await fetchingAPI(url, 'GET', dispatch)
    const prompts = [
      ...rawPrompts.scorecard.map((scorecard) => ({
        ...scorecard,
        type: scorecard.scorecard_configs.reduce((acc, config) => {
          if (!acc.includes(config.type)) {
            acc.push(config.type)
          }
          return acc
        }, []),
        link: scorecard.scorecard_configs.map((scorecardConfig) => ({
          link: `/${scorecardConfig.type === 'copilot' ? 'qa-copilot' : 'coaching/scorecards'}/${scorecardConfig.id}`,
          label: scorecardConfig.name,
        })),
      })),
      ...rawPrompts.notetaker.map((notetaker) => ({
        ...notetaker,
        type: ['notetaker'],
        link: notetaker.playbooks.map((playbook) => ({
          link: `/playbooks/${playbook.id}`,
          label: playbook.name,
        })),
      })),
    ]

    dispatch(setData({ prompts }))
  } catch (err) {
    toast.error('Failed to fetch prompts', { cause: err })
  } finally {
    dispatch(setLoading({ prompts: false }))
  }
}
